import clsx from 'clsx';
import { ComponentProps } from 'react';

export const textVariantClasses: Record<string, string> = {
  // Body
  'body-xs-default': 'text-[12px] leading-[18px] tracking-[-0.2px] font-normal',
  'body-xs-strong': 'text-[12px] leading-[18px] tracking-[-0.2px] font-semibold',
  'body-sm-default': 'text-[14px] leading-[22px] tracking-[-0.2px] font-normal',
  'body-sm-strong': 'text-[14px] leading-[22px] tracking-[-0.2px] font-semibold',
  'body-md-default': 'text-[16px] leading-[24px] tracking-[-0.3px] font-normal',
  'body-md-strong': 'text-[16px] leading-[24px] tracking-[-0.3px] font-semibold',
  'body-lg-default': 'text-[18px] leading-[24px] tracking-[-0.4px] font-normal',
  'body-lg-strong': 'text-[18px] leading-[24px] tracking-[-0.4px] font-semibold',
  'body-xl-default': 'text-[20px] leading-[27px] tracking-[-0.4px] font-normal',
  'body-xl-strong': 'text-[20px] leading-[27px] tracking-[-0.4px] font-semibold',

  // Heading
  'heading-sm-default': 'text-[24px] leading-[34px] tracking-[-0.4px] font-medium',
  'heading-sm-strong': 'text-[24px] leading-[34px] tracking-[-0.4px] font-semibold',
  'heading-md-default': 'text-[32px] leading-[42px] tracking-[-0.75px] font-medium',
  'heading-md-strong': 'text-[32px] leading-[42px] tracking-[-0.75px] font-semibold',
  'heading-lg-default': 'text-[40px] leading-[48px] tracking-[-1.5px] font-medium',
  'heading-lg-strong': 'text-[40px] leading-[48px] tracking-[-1.5px] font-semibold',
  'heading-xl-default': 'text-[48px] leading-[58px] tracking-[-1.6px] font-medium',
  'heading-xl-strong': 'text-[48px] leading-[58px] tracking-[-1.6px] font-semibold',
  'heading-2xl-default': 'text-[56px] leading-[66px] tracking-[-1.75px] font-medium',
  'heading-2xl-strong': 'text-[56px] leading-[66px] tracking-[-1.75px] font-semibold',
  'heading-3xl-default': 'text-[64px] leading-[78px] tracking-[-2.3px] font-medium',
  'heading-3xl-strong': 'text-[64px] leading-[78px] tracking-[-2.3px] font-semibold',

  // Label
  'label-2xs-default': 'text-[10px] leading-[14px] tracking-[-0.2px] font-regular',
  'label-2xs-strong': 'text-[10px] leading-[14px] tracking-[-0.2px] font-medium',
  'label-xs-default': 'text-[12px] leading-[16px] tracking-[-0.2px] font-regular',
  'label-xs-strong': 'text-[12px] leading-[16px] tracking-[-0.2px] font-medium',
  'label-sm-default': 'text-[14px] leading-[20px] tracking-[-0.2px] font-regular',
  'label-sm-strong': 'text-[14px] leading-[20px] tracking-[-0.2px] font-medium',
  'label-md-default': 'text-[16px] leading-[24px] tracking-[-0.3px] font-regular',
  'label-md-strong': 'text-[16px] leading-[24px] tracking-[-0.3px] font-medium',
  'label-lg-default': 'text-[18px] leading-[24px] tracking-[-0.4px] font-regular',
  'label-lg-strong': 'text-[18px] leading-[24px] tracking-[-0.4px] font-medium',
  'label-xl-default': 'text-[20px] leading-[27px] tracking-[-0.4px] font-regular',
  'label-xl-strong': 'text-[20px] leading-[27px] tracking-[-0.4px] font-medium',
  'label-2xl-default': 'text-[24px] leading-[32px] tracking-[-0.4px] font-regular',
  'label-2xl-strong': 'text-[24px] leading-[32px] tracking-[-0.4px] font-medium',

  // Display
  'display-md-default': 'text-[96px] leading-[110px] tracking-[-2.5px] font-regular',
  'display-md-strong': 'text-[96px] leading-[110px] tracking-[-2.5px] font-medium',
};

type TextOwnProps = {
  variant?: 'body' | 'heading' | 'label' | 'display';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  strong?: boolean;
};

type TextTags =
  | 'p'
  | 'span'
  | 'div'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'label'
  | 'a'
  | 'strong';

type TextProps<T extends TextTags = 'p'> = TextOwnProps &
  Omit<ComponentProps<T>, keyof TextOwnProps> &
  (T extends 'p' ? {} : { as: T });

export function Text<T extends TextTags = 'p'>({
  variant = 'body',
  size = 'sm',
  strong = false,
  ...props
}: TextProps<T>) {
  const variantClass = `${variant}-${size}-${strong ? 'strong' : 'default'}`;

  const { as, className, ...restProps } = props as any;
  const Component = as || 'p';

  return (
    <Component {...restProps} className={clsx(textVariantClasses[variantClass], className)}>
      {restProps.children}
    </Component>
  );
}
